import React, { useMemo, useRef } from 'react';
import _get from 'lodash/get';
import _partition from 'lodash/partition';
import { useSelector } from 'react-redux';
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';
import { makeStyles } from '@material-ui/core';
import { useQuery } from 'react-query';
import useSiteCopySelector from 'components/useSiteCopySelector';
import useContentfulLocale from 'components/useContentfulLocale';
import { getCourseSavedResources } from '../getCourseSavedResources';
import { RecommendedResource } from './RecommendedResource';
import { getCourseResourcesSaveForLaterProcessing } from '../selectors';
import { mapSavedSeriesResources } from '../utils';
import RecommendedBooksPreview from './recommended-resources-preview/RecommendedBooksPreview';
import {
  COURSE_SAVED_RECOMMENDED_RESOURCE_KEY,
  COURSE_SAVED_SERIES_RESOURCE_KEY,
} from '../constants';
import { CourseSavedSeriesResource } from './course-saved-series-resource/CourseSavedSeriesResource';

const useStyles = makeStyles(theme => ({
  title: {
    marginBottom: theme.spacing(1),
    color: theme.palette.text.primary,
  },
  button: {
    alignSelf: 'flex-start',
    flexShrink: 0,
    borderRadius: 20,
  },
  seriesResources: {
    display: 'grid',
    columnGap: theme.spacing(1),
    gridTemplateColumns: 'repeat(auto-fill,minmax(255px,1fr))',
    marginTop: theme.spacing(3),
  },
  recommendedContainer: {
    display: 'grid',
    gridTemplateColumns:
      // display 2 columns if width more than 330px, otherwise 1 column
      'repeat(auto-fit, minmax(max(calc(50% - 12px), 330px), 1fr))',
    columnGap: theme.spacing(3),
  },
}));

const CourseSavedResources = ({
  course,
  takenCourse,
  trackingProps,
  removeResourceFromLater,
  goToResource,
  headingVariant,
  small,
}) => {
  const classes = useStyles();
  const savedResourceIds =
    _get(takenCourse, COURSE_SAVED_RECOMMENDED_RESOURCE_KEY) || [];
  const initialSavedResources = useRef(savedResourceIds);

  const savedSeriesResourceIds =
    _get(takenCourse, COURSE_SAVED_SERIES_RESOURCE_KEY) || [];
  const ctfLocaleFilter = useContentfulLocale();
  const [coursesSiteCopy] = useSiteCopySelector(['courses']);
  const courseResourcesSaveForLaterProcessingList = useSelector(
    getCourseResourcesSaveForLaterProcessing,
  );

  const { data: recommendedResources, isFetching } = useQuery(
    initialSavedResources.current.length && [
      'course-saved-resources',
      ctfLocaleFilter,
      initialSavedResources.current,
    ],
    getCourseSavedResources,
  );

  const seriesResources = useMemo(
    () => mapSavedSeriesResources(savedSeriesResourceIds, course),
    [savedSeriesResourceIds, course],
  );
  const filteredSavedResources = (recommendedResources || []).filter(resource =>
    savedResourceIds.includes(_get(resource, 'sys.id')),
  );
  const [bookResources, bookExcludedResources] = _partition(
    filteredSavedResources,
    resource => _get(resource, 'sys.contentType.sys.id') === 'book',
  );

  const handleRemoveResourceFromLater = resource => {
    removeResourceFromLater({
      resource,
      field: COURSE_SAVED_RECOMMENDED_RESOURCE_KEY,
    });
  };

  const handleRemoveSeriesResource = resource => {
    removeResourceFromLater({
      resource,
      field: COURSE_SAVED_SERIES_RESOURCE_KEY,
    });
  };

  const getDefaultRemoveFromLaterButton = ({ isDisabled, resource }) => (
    <Button
      className={classes.button}
      variant="outlined"
      color="primary"
      onClick={() => handleRemoveResourceFromLater(resource)}
      disabled={isDisabled}
    >
      {_get(coursesSiteCopy, 'pageCopy.removeFromSaved')}
    </Button>
  );

  const renderRemoveFromLaterButton = (
    item,
    getRemoveFromLaterButton = getDefaultRemoveFromLaterButton,
  ) => {
    const resourceId = _get(item, 'sys.id');
    const isRemovedResource = !savedResourceIds.includes(resourceId);

    const isResourceLoading = (
      courseResourcesSaveForLaterProcessingList || []
    ).includes(resourceId);

    const isDisabled = isRemovedResource || isResourceLoading;

    return getRemoveFromLaterButton({
      isDisabled,
      resource: item,
      onClick: handleRemoveResourceFromLater,
    });
  };

  const hasResources = bookResources.length || seriesResources.length;
  const goToResourceLabel = _get(coursesSiteCopy, 'pageCopy.goToResource');

  return (
    <Box>
      <Typography variant={headingVariant} className={classes.title}>
        {_get(coursesSiteCopy, 'pageCopy.savedResources')}
      </Typography>
      {isFetching ? (
        <Box display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      ) : (
        <Box>
          {!hasResources && (
            <Box mb={2}>
              <Typography variant="body1">
                {_get(coursesSiteCopy, 'pageCopy.noSavedResources')}
              </Typography>
            </Box>
          )}
          {!!bookExcludedResources.length && (
            <Box className={classes.recommendedContainer}>
              {bookExcludedResources.map(resource => (
                <RecommendedResource
                  key={_get(resource, 'sys.id')}
                  item={resource}
                  course={course}
                  coursesSiteCopy={coursesSiteCopy}
                  saveForLaterButton={renderRemoveFromLaterButton(resource)}
                  trackingProps={trackingProps}
                  isDashboard
                  small={small}
                />
              ))}
            </Box>
          )}
          {!!seriesResources.length && (
            <div className={classes.seriesResources} display="flex">
              {seriesResources.map(data => (
                <CourseSavedSeriesResource
                  key={_get(data, 'resource.sys.id')}
                  data={data}
                  buttonLabel={goToResourceLabel}
                  onRemove={handleRemoveSeriesResource}
                  goToResource={goToResource}
                />
              ))}
            </div>
          )}
          <Box mt={3}>
            <Typography variant={headingVariant} className={classes.title}>
              {_get(coursesSiteCopy, 'pageCopy.myReadingList')}
            </Typography>
            {!bookResources.length && (
              <Typography variant="body1">
                {_get(coursesSiteCopy, 'pageCopy.noSavedReadingList')}
              </Typography>
            )}
          </Box>
          {!!bookResources.length && (
            <RecommendedBooksPreview
              items={bookResources}
              renderButton={renderRemoveFromLaterButton}
              coursesSiteCopy={coursesSiteCopy}
              areSavedResources
            />
          )}
        </Box>
      )}
    </Box>
  );
};

export default CourseSavedResources;
